import { useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { useAuthActions, useSession, type AuthFailure } from "../auth.ts";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import type { SiteCopy } from "../i18n/site.ts";
import { sanitizeNext, useDocumentTitle } from "../routing.ts";
import { Button } from "../ui/Button.tsx";
import { Card } from "../ui/Card.tsx";
import { cn } from "../ui/cn.ts";
import { Field, inputClass } from "../ui/Field.tsx";
import { PageLoading } from "../ui/Spinner.tsx";
import { toast } from "../ui/toast.tsx";

type Mode = "signIn" | "register";
const MODES: Mode[] = ["signIn", "register"];

const failureMessage = (failure: AuthFailure, errors: SiteCopy["login"]["errors"]): string => {
  switch (failure.code) {
    case "INVALID_EMAIL_OR_PASSWORD":
    case "INVALID_PASSWORD":
    case "USER_NOT_FOUND":
    case "CREDENTIAL_ACCOUNT_NOT_FOUND":
      return errors.invalidCredentials;
    case "USER_ALREADY_EXISTS":
    case "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL":
      return errors.userExists;
    case "PASSWORD_TOO_SHORT":
      return errors.passwordTooShort;
    case "PASSWORD_TOO_LONG":
      return errors.passwordTooLong;
    case "INVALID_EMAIL":
      return errors.invalidEmail;
  }
  if (failure.status === 429) return errors.tooManyAttempts;
  if (failure.status === 401) return errors.invalidCredentials;
  return errors.generic;
};

interface LoginPageProps {
  /** Replaces the course-site intro above each form (already localized). */
  intro?: { signIn: string; register: string };
}

/** `/inloggen?next=…`: sign in or create an account, then return to `next`. */
export const LoginPage = ({ intro }: LoginPageProps): ReactNode => {
  const copy = useSiteCopy();
  const { user, loading } = useSession();
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get("next"));
  const [mode, setMode] = useState<Mode>("signIn");
  const [submitted, setSubmitted] = useState(false);
  const tabRefs = useRef<Record<Mode, HTMLButtonElement | null>>({ signIn: null, register: null });
  const baseId = useId();
  useDocumentTitle(copy.login.title);

  if (loading) return <PageLoading label={copy.common.loading} />;
  // Already signed in (or just did): the form's own navigate wins; this covers direct visits.
  if (user && !submitted) return <Navigate to={next} replace />;

  const tabLabel: Record<Mode, string> = {
    signIn: copy.login.signInTab,
    register: copy.login.registerTab,
  };

  const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const other: Mode = mode === "signIn" ? "register" : "signIn";
    setMode(other);
    tabRefs.current[other]?.focus();
  };

  return (
    <div className="mx-auto max-w-md px-4 py-10 sm:py-16">
      <h1 className="text-4xl font-semibold">{copy.login.title}</h1>
      <Card className="mt-6 p-0 sm:p-0">
        <div
          role="tablist"
          aria-label={copy.login.tabsLabel}
          className="grid grid-cols-2 border-b border-border">
          {MODES.map((tab) => (
            <button
              key={tab}
              ref={(element) => {
                tabRefs.current[tab] = element;
              }}
              type="button"
              role="tab"
              id={`${baseId}-${tab}-tab`}
              aria-selected={mode === tab}
              aria-controls={`${baseId}-${tab}-panel`}
              tabIndex={mode === tab ? 0 : -1}
              onClick={() => setMode(tab)}
              onKeyDown={handleTabKey}
              className={cn(
                "-mb-px min-h-13 border-b-3 px-3 text-base font-semibold transition-colors",
                "first:rounded-tl-2xl last:rounded-tr-2xl",
                mode === tab
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}>
              {tabLabel[tab]}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          id={`${baseId}-${mode}-panel`}
          aria-labelledby={`${baseId}-${mode}-tab`}
          className="p-5 sm:p-6">
          <AuthForm
            key={mode}
            mode={mode}
            next={next}
            intro={
              mode === "register"
                ? (intro?.register ?? copy.login.registerIntro)
                : (intro?.signIn ?? copy.login.signInIntro)
            }
            onSubmitted={() => setSubmitted(true)}
          />
        </div>
      </Card>
    </div>
  );
};

interface AuthFormProps {
  mode: Mode;
  next: string;
  intro: string;
  onSubmitted: () => void;
}

const AuthForm = ({ mode, next, intro, onSubmitted }: AuthFormProps): ReactNode => {
  const copy = useSiteCopy().login;
  const { signIn, signUp } = useAuthActions();
  const navigate = useNavigate();
  const id = useId();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const register = mode === "register";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    onSubmitted();
    try {
      const result = register
        ? await signUp({ name: name.trim(), email: email.trim(), password })
        : await signIn({ email: email.trim(), password });
      if (!result.ok) {
        setError(failureMessage(result.error, copy.errors));
        return;
      }
      if (register) toast.success(copy.welcome(name.trim()));
      void navigate(next, { replace: true });
    } catch {
      setError(copy.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={(event) => void handleSubmit(event)}>
      <p className="text-muted-foreground">{intro}</p>
      {register && (
        <Field label={copy.name} htmlFor={`${id}-name`}>
          <input
            id={`${id}-name`}
            className={inputClass}
            autoComplete="name"
            required
            maxLength={100}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      )}
      <Field label={copy.email} htmlFor={`${id}-email`}>
        <input
          id={`${id}-email`}
          type="email"
          className={inputClass}
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field
        label={copy.password}
        htmlFor={`${id}-password`}
        hint={register ? copy.passwordHint : undefined}>
        <input
          id={`${id}-password`}
          type="password"
          className={inputClass}
          autoComplete={register ? "new-password" : "current-password"}
          required
          minLength={register ? 8 : undefined}
          maxLength={128}
          aria-describedby={register ? `${id}-password-hint` : undefined}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <div aria-live="assertive">
        {error && (
          <p
            className={cn(
              "rounded-xl border border-danger/40 bg-danger/8 px-4 py-3 font-medium",
              "text-danger",
            )}>
            {error}
          </p>
        )}
      </div>
      <Button type="submit" size="lg" loading={busy}>
        {register ? copy.submitRegister : copy.submitSignIn}
      </Button>
    </form>
  );
};
